import React from 'react';
import { Target, ChevronDown, ChevronUp, Play, Pause } from 'lucide-react';
import { formatDateTime, formatCurrency, formatCurrencyCompact, formatNumber, formatNumberCompact, formatPercentage, formatConversionTypeName, getConversionData } from '../utils/helpers';

const MetaCampaigns = ({ 
  event, 
  matchedCampaigns, 
  expandedSections, 
  expandedAdSets,
  toggleSection,
  toggleAdSetExpansion 
}) => {
  if (!matchedCampaigns || matchedCampaigns.length === 0) return null;
  
  // Get the section expanded state for this specific event
  const sectionExpanded = expandedSections[event.id] || {};

  return (
    <div className="border-t dark:border-gray-700 pt-3" data-meta-section={event.id}>
      <div 
        className="flex items-center justify-between mb-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          toggleSection(event.id, 'metaCampaigns');
        }}
      >
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <Target className="w-3 h-3 mr-1.5 text-purple-600 dark:text-purple-400" />
          Meta Campaigns ({matchedCampaigns.length})
        </h4>
        <div className="p-0.5">
          {sectionExpanded.metaCampaigns ? (
            <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
          )}
        </div>
      </div>
      {sectionExpanded.metaCampaigns && (
        <div className="space-y-2">
          {matchedCampaigns.map(campaign => {
            const campaignKey = `meta-${event.id}-${campaign.id}`;
            const isCampaignExpanded = expandedSections[campaignKey]?.campaign === true;
            
            // Calculate campaign totals from ad sets - FIXED TO MATCH APP-FLAT.JS
            const campaignTotals = (campaign.adsets || []).reduce((totals, adset) => {
              const insights = adset.insights?.data?.[0] || {};
              const conversionData = getConversionData(insights);
              
              // FIX: Calculate purchase conversions exactly like App-Flat.js
              const purchaseConversions = Object.entries(conversionData.conversions)
                .filter(([type]) => type === 'purchase')
                .reduce((sum, [, conv]) => sum + conv.count, 0);
              
              return {
                spend: totals.spend + parseFloat(insights.spend || 0),
                impressions: totals.impressions + parseInt(insights.impressions || 0),
                clicks: totals.clicks + parseInt(insights.clicks || 0),
                reach: totals.reach + parseInt(insights.reach || 0), // FIX: Add reach like App-Flat.js
                conversions: totals.conversions + purchaseConversions,
                conversionValue: totals.conversionValue + conversionData.totalConversionValue,
                purchaseValue: totals.purchaseValue + conversionData.totalPurchaseValue // FIX: Added purchaseValue
              };
            }, { spend: 0, impressions: 0, clicks: 0, reach: 0, conversions: 0, conversionValue: 0, purchaseValue: 0 }); // FIX: Added purchaseValue: 0
            
            // Calculate metrics
            const campaignCTR = campaignTotals.impressions > 0 
              ? (campaignTotals.clicks / campaignTotals.impressions) * 100 
              : 0;
            const campaignPurchaseRate = campaignTotals.clicks > 0 
              ? (campaignTotals.conversions / campaignTotals.clicks) * 100 
              : 0;
            const campaignCostPerPurchase = campaignTotals.conversions > 0 
              ? campaignTotals.spend / campaignTotals.conversions 
              : 0;
            // FIX: ROAS calculation to match App-Flat.js exactly
            const campaignROAS = campaignTotals.spend > 0 && campaignTotals.purchaseValue > 0
              ? campaignTotals.purchaseValue / campaignTotals.spend
              : 0;
            
            // Add calculated metrics to totals
            campaignTotals.cpm = campaignTotals.impressions > 0 
              ? (campaignTotals.spend / campaignTotals.impressions) * 1000 
              : 0;
            campaignTotals.cpc = campaignTotals.clicks > 0 
              ? campaignTotals.spend / campaignTotals.clicks 
              : 0;
            campaignTotals.frequency = campaignTotals.reach > 0 
              ? campaignTotals.impressions / campaignTotals.reach 
              : 0;
            
            return (
              <div key={campaign.id} className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded-lg">
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <h5 className="font-medium text-purple-900 dark:text-purple-100 text-xs">
                        {campaign.name}
                      </h5>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection(campaignKey, 'campaign');
                        }}
                        className="p-0.5 hover:bg-purple-100 dark:hover:bg-purple-800/30 rounded transition-colors"
                      >
                        {isCampaignExpanded ? (
                          <ChevronUp className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <ChevronDown className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-xs text-purple-700 dark:text-purple-300">
                      <span className={`flex items-center gap-0.5 ${
                        campaign.effective_status === 'ACTIVE' ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {campaign.effective_status === 'ACTIVE' ? (
                          <Play className="w-2.5 h-2.5" />
                        ) : (
                          <Pause className="w-2.5 h-2.5" />
                        )}
                        {campaign.effective_status}
                      </span>
                      <span className="text-purple-600 dark:text-purple-400">•</span>
                      <span>Started: {formatDateTime(campaign.start_time)}</span>
                      {campaign.stop_time && (
                        <span key={`stop-${campaign.id}`}>
                          <span className="text-purple-600 dark:text-purple-400">•</span>
                          {' '}Ends: {formatDateTime(campaign.stop_time)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Campaign Summary Stats - Always Visible */}
                {campaignTotals.spend > 0 && (
                  <div className="bg-white dark:bg-gray-800 p-1.5 rounded border border-purple-200 dark:border-purple-800">
                    <div className="text-xs font-medium text-purple-800 dark:text-purple-200 mb-0.5">Campaign Totals:</div>
                    <div className="space-y-2">
                      {/* Traffic Section */}
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Traffic:</div>
                        {/* Row 1: Spend, Impressions, Reach */}
                        <div className="grid grid-cols-3 gap-0.5 text-xs mb-0.5">
                          <div className="text-center bg-red-50 dark:bg-red-900/20 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Spend</div>
                            <div className="font-medium text-red-600 dark:text-red-400">{formatCurrency(campaignTotals.spend)}</div>
                          </div>
                          <div className="text-center bg-blue-50 dark:bg-blue-900/20 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Impressions</div>
                            <div className="font-medium text-blue-600 dark:text-blue-400">{formatNumberCompact(campaignTotals.impressions)}</div>
                          </div>
                          <div className="text-center bg-indigo-50 dark:bg-indigo-900/20 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Reach</div>
                            <div className="font-medium text-indigo-600 dark:text-indigo-400">{formatNumberCompact(campaignTotals.reach)}</div>
                          </div>
                        </div>
                        
                        {/* Row 2: Clicks, CPM, CPC */}
                        <div className="grid grid-cols-3 gap-0.5 text-xs mb-0.5">
                          <div className="text-center bg-blue-50 dark:bg-blue-900/20 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Clicks</div>
                            <div className="font-medium text-blue-600 dark:text-blue-400">{formatNumberCompact(campaignTotals.clicks)}</div>
                          </div>
                          <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">CPM</div>
                            <div className="font-medium dark:text-gray-100">{formatCurrency(campaignTotals.cpm)}</div>
                          </div>
                          <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">CPC</div>
                            <div className="font-medium dark:text-gray-100">{formatCurrency(campaignTotals.cpc)}</div>
                          </div>
                        </div>
                        
                        {/* Row 3: CTR, Frequency */}
                        <div className="grid grid-cols-2 gap-0.5 text-xs">
                          <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">CTR</div>
                            <div className="font-medium dark:text-gray-100">{formatPercentage(campaignCTR)}</div>
                          </div>
                          <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Frequency</div>
                            <div className="font-medium dark:text-gray-100">{campaignTotals.frequency.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Conversions Section */}
                      <div className="border-t dark:border-gray-700 pt-1">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Conversions:</div>
                        {/* Row 1: Purchases */}
                        <div className="grid grid-cols-1 gap-0.5 text-xs mb-0.5">
                          <div className="text-center bg-green-50 dark:bg-green-900/20 p-0.5 rounded">
                            <div className="text-gray-500 dark:text-gray-400 text-xs">Purchases</div>
                            <div className="font-medium text-green-600 dark:text-green-400">{formatNumber(campaignTotals.conversions)}</div>
                          </div>
                        </div>
                        
                        {/* Revenue & ROAS Row */}
                        {(campaignTotals.conversionValue > 0 || campaignROAS > 0) && (
                          <div className="grid grid-cols-2 gap-0.5 text-xs mb-0.5">
                            {campaignTotals.conversionValue > 0 && (
                              <div className="text-center bg-emerald-50 dark:bg-emerald-900/20 p-0.5 rounded">
                                <div className="text-gray-500 dark:text-gray-400 text-xs">Value</div>
                                <div className="font-medium text-emerald-600 dark:text-emerald-400">{formatCurrency(campaignTotals.conversionValue)}</div>
                              </div>
                            )}
                            {campaignROAS > 0 && (
                              <div className={`text-center p-0.5 rounded ${
                                campaignROAS >= 4 ? 'bg-green-50 dark:bg-green-900/20' :
                                campaignROAS >= 2 ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                                'bg-red-50 dark:bg-red-900/20'
                              }`}>
                                <div className="text-gray-500 dark:text-gray-400 text-xs">ROAS</div>
                                <div className={`font-medium ${
                                  campaignROAS >= 4 ? 'text-green-600 dark:text-green-400' :
                                  campaignROAS >= 2 ? 'text-yellow-600 dark:text-yellow-400' :
                                  'text-red-600 dark:text-red-400'
                                }`}>
                                  {campaignROAS.toFixed(2)}x
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Rates Row */}
                        {campaignTotals.conversions > 0 && (
                          <div className="grid grid-cols-2 gap-0.5 text-xs">
                            <div className="text-center bg-purple-50 dark:bg-purple-900/20 p-0.5 rounded">
                              <div className="text-gray-500 dark:text-gray-400 text-xs">Purch Rate</div>
                              <div className="font-medium text-purple-600 dark:text-purple-400">{formatPercentage(campaignPurchaseRate)}</div>
                            </div>
                            <div className="text-center bg-orange-50 dark:bg-orange-900/20 p-0.5 rounded">
                              <div className="text-gray-500 dark:text-gray-400 text-xs">Cost/Purch</div>
                              <div className="font-medium text-orange-600 dark:text-orange-400">{formatCurrency(campaignCostPerPurchase)}</div>
                            </div>
                          </div>
                        )}
                        
                        {campaignTotals.conversions === 0 && (
                          <div className="text-center text-xs text-gray-400 dark:text-gray-500 italic py-0.5">
                            No purchases yet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Ad Sets - Only visible when campaign is expanded */}
                {isCampaignExpanded && campaign.adsets && campaign.adsets.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-purple-800 dark:text-purple-200 mb-1">
                      Ad Sets ({campaign.adsets.length}):
                    </p>
                    <div className="space-y-0.5">
                      {campaign.adsets.map(adset => {
                        const insights = adset.insights?.data?.[0] || {};
                        const spend = parseFloat(insights.spend || 0);
                        const impressions = parseInt(insights.impressions || 0);
                        const clicks = parseInt(insights.clicks || 0);
                        const ctr = parseFloat(insights.ctr || 0);
                        const cpm = parseFloat(insights.cpm || 0);
                        const reach = parseInt(insights.reach || 0);
                        const conversionData = getConversionData(insights);
                        const conversionRanking = insights.conversion_rate_ranking || 'unknown';
                        
                        // Calculate only purchase conversions
                        const purchaseConversions = Object.entries(conversionData.conversions)
                          .filter(([type]) => type === 'purchase')
                          .reduce((sum, [, conv]) => sum + conv.count, 0);
                        
                        const purchaseConversionRate = clicks > 0 ? (purchaseConversions / clicks) * 100 : 0;
                        const costPerPurchase = purchaseConversions > 0 ? spend / purchaseConversions : 0;
                        
                        const adSetKey = `${campaign.id}-${adset.id}`;
                        const isAdSetExpanded = expandedAdSets[adSetKey] === true;
                        
                        return (
                          <div key={adset.id} className="bg-white dark:bg-gray-800 rounded border-l-2 border-l-purple-300 dark:border-l-purple-600 overflow-hidden">
                            {/* Ad Set Header - Always Visible */}
                            <div className="p-2">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0 pr-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 text-xs whitespace-nowrap overflow-hidden text-ellipsis">
                                    {adset.name}
                                  </div>
                                  <div className="text-gray-600 dark:text-gray-400 text-xs mt-0.5">
                                    {formatDateTime(adset.start_time)} → {adset.end_time ? formatDateTime(adset.end_time) : 'Ongoing'}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                                  {conversionRanking && conversionRanking.toLowerCase() !== 'unknown' && (
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                                      conversionRanking === 'above_average' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                      conversionRanking === 'average' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                      conversionRanking === 'below_average' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                                      'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    }`}>
                                      {conversionRanking.replace('_', ' ')}
                                    </span>
                                  )}
                                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                                    adset.effective_status === 'ACTIVE' 
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                  }`}>
                                    {adset.effective_status}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleAdSetExpansion(campaign.id, adset.id);
                                    }}
                                    className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                  >
                                    {isAdSetExpanded ? (
                                      <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                    ) : (
                                      <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                    )}
                                  </button>
                                </div>
                              </div>
                              
                              {/* Quick Stats - Always Visible */}
                              {spend > 0 && (
                                <div className="grid grid-cols-4 gap-1 mt-1.5 text-xs">
                                  <div className="text-center">
                                    <div className="text-gray-500 dark:text-gray-400 text-xs">Spend</div>
                                    <div className="font-medium text-red-600 dark:text-red-400">{formatCurrency(spend)}</div>
                                  </div>
                                  {conversionData.totalPurchaseValue > 0 ? (
                                    <div className="text-center">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Value</div>
                                      <div className="font-medium text-emerald-600 dark:text-emerald-400">{formatCurrencyCompact(conversionData.totalPurchaseValue)}</div>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Value</div>
                                      <div className="font-medium text-gray-400 dark:text-gray-500">—</div>
                                    </div>
                                  )}
                                  {conversionData.roas > 0 ? (
                                    <div className="text-center">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">ROAS</div>
                                      <div className={`font-medium ${
                                        conversionData.roas >= 4 ? 'text-green-600 dark:text-green-400' :
                                        conversionData.roas >= 2 ? 'text-yellow-600 dark:text-yellow-400' :
                                        'text-red-600 dark:text-red-400'
                                      }`}>{conversionData.roas.toFixed(1)}x</div>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">ROAS</div>
                                      <div className="font-medium text-gray-400 dark:text-gray-500">—</div>
                                    </div>
                                  )}
                                  {purchaseConversions > 0 ? (
                                    <div className="text-center">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Purch</div>
                                      <div className="font-medium text-green-600 dark:text-green-400">{formatNumber(purchaseConversions)}</div>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Purch</div>
                                      <div className="font-medium text-gray-400 dark:text-gray-500">—</div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Expanded Performance Metrics */}
                            {isAdSetExpanded && spend > 0 && (
                              <div className="border-t border-gray-200 dark:border-gray-700 p-2 bg-gray-50 dark:bg-gray-900/50 space-y-2">
                                {/* Traffic Section */}
                                <div>
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Traffic:</div>
                                  {/* Row 1: Spend, Impressions, Reach */}
                                  <div className="grid grid-cols-3 gap-1 text-xs mb-0.5">
                                    <div className="text-center bg-red-50 dark:bg-red-900/20 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Spend</div>
                                      <div className="font-medium text-red-600 dark:text-red-400">{formatCurrency(spend)}</div>
                                    </div>
                                    <div className="text-center bg-blue-50 dark:bg-blue-900/20 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Impressions</div>
                                      <div className="font-medium text-blue-600 dark:text-blue-400">{formatNumberCompact(impressions)}</div>
                                    </div>
                                    <div className="text-center bg-indigo-50 dark:bg-indigo-900/20 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Reach</div>
                                      <div className="font-medium text-indigo-600 dark:text-indigo-400">{formatNumberCompact(reach)}</div>
                                    </div>
                                  </div>
                                  
                                  {/* Row 2: Clicks, CPM, CPC */}
                                  <div className="grid grid-cols-3 gap-1 text-xs mb-0.5">
                                    <div className="text-center bg-blue-50 dark:bg-blue-900/20 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Clicks</div>
                                      <div className="font-medium text-blue-600 dark:text-blue-400">{formatNumberCompact(clicks)}</div>
                                    </div>
                                    <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">CPM</div>
                                      <div className="font-medium dark:text-gray-100">{formatCurrency(cpm)}</div>
                                    </div>
                                    <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">CPC</div>
                                      <div className="font-medium dark:text-gray-100">{formatCurrency(clicks > 0 ? spend / clicks : 0)}</div>
                                    </div>
                                  </div>
                                  
                                  {/* Row 3: CTR, Frequency */}
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">CTR</div>
                                      <div className="font-medium dark:text-gray-100">{formatPercentage(ctr)}</div>
                                    </div>
                                    <div className="text-center bg-gray-50 dark:bg-gray-700/50 p-0.5 rounded">
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Frequency</div>
                                      <div className="font-medium dark:text-gray-100">{reach > 0 ? (impressions / reach).toFixed(2) : '0.00'}</div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Conversions Section */}
                                <div className="border-t dark:border-gray-700 pt-1">
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Conversions:</div>
                                  {purchaseConversions > 0 ? (
                                    <>
                                      {/* Purchases Count */}
                                      <div className="grid grid-cols-1 gap-1 text-xs mb-1">
                                        <div className="text-center bg-green-50 dark:bg-green-900/20 p-0.5 rounded">
                                          <div className="text-gray-500 dark:text-gray-400 text-xs">Purchases</div>
                                          <div className="font-medium text-green-600 dark:text-green-400">{formatNumber(purchaseConversions)}</div>
                                        </div>
                                      </div>
                                      
                                      {/* Revenue and ROAS if available */}
                                      {(conversionData.totalPurchaseValue > 0 || conversionData.roas > 0) && (
                                        <div className="grid grid-cols-2 gap-1 text-xs mb-1">
                                          {conversionData.totalPurchaseValue > 0 && (
                                            <div className="text-center bg-emerald-50 dark:bg-emerald-900/20 p-0.5 rounded">
                                              <div className="text-gray-500 dark:text-gray-400 text-xs">Value</div>
                                              <div className="font-medium text-emerald-600 dark:text-emerald-400">{formatCurrency(conversionData.totalPurchaseValue)}</div>
                                            </div>
                                          )}
                                          {conversionData.roas > 0 && (
                                            <div className={`text-center p-0.5 rounded ${
                                              conversionData.roas >= 4 ? 'bg-green-50 dark:bg-green-900/20' :
                                              conversionData.roas >= 2 ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                                              'bg-red-50 dark:bg-red-900/20'
                                            }`}>
                                              <div className="text-gray-500 dark:text-gray-400 text-xs">ROAS</div>
                                              <div className={`font-medium ${
                                                conversionData.roas >= 4 ? 'text-green-600 dark:text-green-400' :
                                                conversionData.roas >= 2 ? 'text-yellow-600 dark:text-yellow-400' :
                                                'text-red-600 dark:text-red-400'
                                              }`}>
                                                {conversionData.roas.toFixed(2)}x
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Purchase Rate and Cost/Purchase */}
                                      <div className="grid grid-cols-2 gap-1 text-xs">
                                        <div className="text-center bg-purple-50 dark:bg-purple-900/20 p-0.5 rounded">
                                          <div className="text-gray-500 dark:text-gray-400 text-xs">Purch Rate</div>
                                          <div className="font-medium text-purple-600 dark:text-purple-400">{formatPercentage(purchaseConversionRate)}</div>
                                        </div>
                                        <div className="text-center bg-orange-50 dark:bg-orange-900/20 p-0.5 rounded">
                                          <div className="text-gray-500 dark:text-gray-400 text-xs">Cost/Purchase</div>
                                          <div className="font-medium text-orange-600 dark:text-orange-400">{formatCurrency(costPerPurchase)}</div>
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-center text-xs text-gray-400 dark:text-gray-500 italic py-1">
                                      No purchases recorded yet
                                    </div>
                                  )}
                                  
                                  {/* Engagement Breakdown - Collapsible */}
                                  {Object.keys(conversionData.conversions).length > 1 && (
                                    <div className="mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
                                      <div className="flex items-center justify-between mb-0.5">
                                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Engagement:</div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSection(`${campaign.id}-${adset.id}`, 'conversionBreakdown');
                                          }}
                                          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                        >
                                          {expandedSections[`${campaign.id}-${adset.id}`]?.conversionBreakdown ? (
                                            <ChevronUp className="w-2.5 h-2.5 text-gray-600 dark:text-gray-400" />
                                          ) : (
                                            <ChevronDown className="w-2.5 h-2.5 text-gray-600 dark:text-gray-400" />
                                          )}
                                        </button>
                                      </div>
                                      {expandedSections[`${campaign.id}-${adset.id}`]?.conversionBreakdown && (
                                        <div className="space-y-0.5">
                                          {(() => {
                                            const engagementMetrics = {
                                              'onsite_conversion.messaging_conversation_started_7d': 'Messaging Conversation Started 7d',
                                              'onsite_web_app_purchase': 'Web In Store Purchase',
                                              'initiate_checkout': 'Checkout Started',
                                              'omni_initiated_checkout': 'Checkout Started',
                                              'landing_page_view': 'Landing Views',
                                              'omni_landing_page_view': 'Landing Views',
                                              'onsite_conversion.post_save': 'Saved Posts',
                                              'post': 'Post',
                                              'post_reaction': 'Post Reaction',
                                              'link_click': 'Link Click'
                                            };
                                            
                                            const items = Object.entries(conversionData.conversions)
                                              .filter(([type]) => type !== 'purchase' && engagementMetrics[type])
                                              .sort(([, a], [, b]) => b.count - a.count)
                                              .map(([type, data]) => (
                                                <div key={type} className="flex justify-between items-center py-0.5 px-1 bg-gray-50 dark:bg-gray-700/50 rounded text-xs">
                                                  <span className="text-gray-600 dark:text-gray-400 text-xs">
                                                    {engagementMetrics[type] || formatConversionTypeName(type)}
                                                  </span>
                                                  <span className="font-medium dark:text-gray-100">{formatNumber(data.count)}</span>
                                                </div>
                                              ));
                                            
                                            return items.length > 0 ? items : null;
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MetaCampaigns;
